/******/ (() => { // webpackBootstrap
  class ProductCardBasic extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('#ProductCardBasicAtcForm');
      this.quickViewLink = this.querySelector('[data-quick-view-link]');
      this.cartCountIndicator = document.querySelector('[data-cart-count-indicator]');
      this.attachEvents = this.attachEvents.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.updateCartCountIndicator = this.updateCartCountIndicator.bind(this);
      this.handleQuickView = this.handleQuickView.bind(this);
      this.showUpsellAddedNotice = this.showUpsellAddedNotice.bind(this);
      this.wethemeGlobal = document.querySelector('script#wetheme-global');
      this.translationsObject = JSON.parse(this.wethemeGlobal.textContent);
      this.addedResetTimer = null;
    }
  
    connectedCallback() {
      window.wetheme.webcomponentRegistry.register({key: 'component-product-card-basic'});
      this.attachEvents();
    }
  
    attachEvents() {
      if (this.form) {
        this.form.addEventListener('submit', this.handleSubmit);
      }
  
      if (this.quickViewLink) {
        this.quickViewLink.addEventListener('click', this.handleQuickView);
      }
    }

    isCartUpsellCard() {
      return Boolean(this.closest('.you-may-also-like, [data-cart-drawer-recommendations]'));
    }
  
    async handleSubmit(e) {
      e.preventDefault();
  
      const addButton = e.currentTarget.querySelector('button');
      const labelEl = addButton?.querySelector('span') || addButton;
      const originalLabel = labelEl ? labelEl.textContent.trim() : '';
      const isUpsell = this.isCartUpsellCard();
      const variantId =
        this.form?.querySelector('[data-variant-id]')?.value ||
        this.form?.querySelector('[name="id"]')?.value ||
        null;
      const productTitle =
        this.closest('.product-card-basic')?.querySelector('.cart-rec-product-title, .indiv-product-title-text')?.textContent?.trim() ||
        '';

      const upsellLog = (step, detail) => {
        if (typeof window.__upsellCartLog === 'function') {
          window.__upsellCartLog(step, detail);
        } else if (window.__UPSELL_CART_DEBUG !== false) {
          console.log('[upsell-cart]', step, detail || {});
        }
      };

      if (isUpsell) {
        upsellLog('card.submit.start', {
          variantId,
          productTitle,
          cartAddUrl: window.routes?.cart_add_url || null,
          hasRefresh: typeof window.refreshCustomCartDrawerFromCart === 'function',
          hasSharedCart: typeof window.getSharedCartJs === 'function',
          dom: typeof window.__upsellCartSnapshotDom === 'function'
            ? window.__upsellCartSnapshotDom()
            : null
        });
      }

      if (addButton) {
        addButton.disabled = true;
        addButton.style.width = `${addButton.getBoundingClientRect().width}px`;
        addButton.style.height = `${addButton.getBoundingClientRect().height}px`;
      }
      
      try {
        const response = await fetch(window.routes.cart_add_url, this.fetchConfigWithBody());
        const responseJson = await response.json();

        if (isUpsell) {
          upsellLog('card.submit.addResponse', {
            ok: response.ok,
            status: response.status,
            variantId,
            responseId: responseJson?.id || responseJson?.variant_id || null,
            responseKey: responseJson?.key || null,
            responseTitle: responseJson?.product_title || responseJson?.title || null,
            responseQty: responseJson?.quantity || null,
            itemsLen: Array.isArray(responseJson?.items) ? responseJson.items.length : null,
            blocked: !!responseJson?.blocked,
            error: responseJson?.description || responseJson?.message || null
          });
        }

        if (!response.ok) {
          console.error('Unable to add to cart: ', responseJson);
          if (addButton) addButton.disabled = false;
          return;
        }

        // Upsell add mutates cart — drop shared cache before refresh or we
        // may redraw from a stale pre-add /cart.js snapshot (within TTL).
        if (isUpsell && typeof window.invalidateSharedCartJs === 'function') {
          window.invalidateSharedCartJs();
          upsellLog('card.submit.invalidateSharedCart', { variantId });
        }

        window.eventBus && window.eventBus.emit('update:cart:drawer', responseJson);
        this.updateCartCountIndicator();

        if (isUpsell) {
          // Single coalesced refresh — avoid stacking refresh + scheduleAuthoritativeCartRedraw.
          if (typeof window.refreshCustomCartDrawerFromCart === 'function') {
            upsellLog('card.submit.refreshCall', {
              label: 'upsell product-card-basic',
              variantId
            });
            window.refreshCustomCartDrawerFromCart('upsell product-card-basic');
          } else {
            upsellLog('card.submit.refreshMissing', { variantId });
          }

          this.showUpsellAddedNotice(addButton, labelEl, originalLabel, responseJson);

          const cartBody = document.querySelector('#cartSlideoutWrapper .cart-drawer-body');
          if (cartBody) {
            cartBody.scrollTo({ top: 0, behavior: 'smooth' });
          }
        } else if (addButton) {
          addButton.disabled = false;
          addButton.style.width = '';
          addButton.style.height = '';
        }
      } catch (err) {
        console.error('Unable to add to cart: ', err);
        if (isUpsell) {
          upsellLog('card.submit.error', {
            variantId,
            error: String(err && err.message ? err.message : err)
          });
        }
        if (addButton) {
          addButton.disabled = false;
          addButton.style.width = '';
          addButton.style.height = '';
        }
      }
    }

    showUpsellAddedNotice(addButton, labelEl, originalLabel, responseJson) {
      if (!addButton || !labelEl) return;

      const addedLabel =
        this.translationsObject?.translations?.added || 'Added';
      const productTitle =
        responseJson?.product_title ||
        responseJson?.title ||
        responseJson?.items?.[0]?.product_title ||
        '';

      if (this.addedResetTimer) {
        clearTimeout(this.addedResetTimer);
      }

      labelEl.textContent = addedLabel;
      addButton.classList.add('is-added');
      addButton.setAttribute('aria-live', 'polite');

      const notice = document.getElementById('cart-rec-add-notice');
      if (notice) {
        notice.textContent = productTitle
          ? `${addedLabel}: ${productTitle}`
          : `${addedLabel} to cart`;
        notice.classList.add('is-visible');
      }

      this.addedResetTimer = setTimeout(() => {
        labelEl.textContent = originalLabel;
        addButton.classList.remove('is-added');
        addButton.disabled = false;
        addButton.style.width = '';
        addButton.style.height = '';
        if (notice) {
          notice.classList.remove('is-visible');
          notice.textContent = '';
        }
        this.addedResetTimer = null;
      }, 2200);
    }
  
    fetchConfigWithBody() {
      /* ===== Create the fetch configuration object with the form data as the body ===== */
      const config = this.fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      const body = new FormData(this.form);
      // Cards use data-variant-id (not name=id) so MageWorx PDP selectors stay correct.
      const variantId =
        this.form.querySelector('[data-variant-id]')?.value ||
        this.form.querySelector('[name="id"]')?.value;
      if (variantId && !body.get('id')) {
        body.set('id', variantId);
      }
      config.body = body;
      return config;
    }
  
    fetchConfig(type = 'json') {
      /* ===== Create the fetch configuration object ===== */
      return {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
      };
    }
  
    async getCart() {
      /* ===== Fetch the cart information ===== */
      try {
        const response = await fetch(`${window.routes.cart_url}?view=compare`);
        return await response.json();
      } catch (error) {
        console.error(`Error fetching cart: ${error.description || error.message}`);
      }
    }
  
    updateCartCountIndicator() {
      /* ===== Update the cart count indicator ===== */
      if (!this.cartCountIndicator) return;
      this.getCart().then(cart => window.wetheme.updateCartCount(cart));
    }
  
    handleQuickView(e) {
      e.preventDefault();
  
      /* ===== Close cart drawer ===== */
      window.eventBus && window.eventBus.emit('close:all:drawers');
  
      /* ===== Open quick view drawer ===== */
      window.wetheme.toggleRightDrawer('shop-now', true, { url: e.currentTarget.href });
    }
  
    detachEvents() {
      if (this.form) {
        this.form.removeEventListener('submit', this.handleSubmit);
      }
  
      if (this.quickViewLink) {
        this.quickViewLink.removeEventListener('click', this.handleQuickView);
      }
    }
  
    disconnectedCallback() {
      if (this.addedResetTimer) {
        clearTimeout(this.addedResetTimer);
      }
      this.detachEvents();
    }
  }
  
  customElements.define('product-card-basic', ProductCardBasic);
  /******/ })()
  ;
